/**
 * PWA runtime: SW registration + the auto-update-with-toast flow, durable
 * storage request, and app-shortcut actions (?action=new-note|daily).
 */
import { registerSW } from "virtual:pwa-register";
import { toast } from "@/ui/toast";
import { createNote } from "@/commands/nav";
import { openDailyNote } from "@/commands/templates";

const UPDATE_CHECK_MS = 5 * 60_000;
/** How long the tab must stay hidden before a pending update applies itself. */
const HIDDEN_APPLY_MS = 10_000;

export function initPwa(): void {
  if (import.meta.env.DEV) return;

  let pending = false;
  let hiddenTimer: ReturnType<typeof setTimeout> | undefined;

  /** Swap to the waiting worker, then reload once it has taken over. */
  function applyUpdate(): void {
    if (!pending) return;
    pending = false;
    // own the reload: workbox-window's auto-reload flags do not fire for
    // updates found by our own polling
    navigator.serviceWorker?.addEventListener("controllerchange", () => location.reload(), { once: true });
    void updateSW(false);
    setTimeout(() => location.reload(), 2500); // fallback either way
  }

  const updateSW = registerSW({
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;
      // The browser only checks for a new SW on navigation, which is useless
      // for a long-lived app. Poll while the tab lives, and check on return
      // to the tab or to the network, so the toast appears in near real time.
      const check = async () => {
        if (registration.installing || !navigator.onLine) return;
        try {
          const resp = await fetch(swUrl, { cache: "no-store", headers: { "cache-control": "no-cache" } });
          if (resp.ok) await registration.update();
        } catch {
          // offline or server briefly away, the next tick retries
        }
      };

      // Check straight away. Without this, a tab that is opened, used and
      // closed inside the poll interval never learns a new build exists,
      // which is how a browser ends up serving one build for days.
      void check();
      setInterval(() => void check(), UPDATE_CHECK_MS);
      window.addEventListener("online", () => void check());
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          clearTimeout(hiddenTimer);
          void check();
        } else if (pending) {
          // Apply it while nobody is looking. Notes are CRDT-backed and saved
          // continuously, so a reload costs nothing, and this is what stops
          // "Reload to apply" from being ignored until the build is ancient.
          hiddenTimer = setTimeout(applyUpdate, HIDDEN_APPLY_MS);
        }
      });
    },
    onNeedRefresh() {
      // background-installed update; never interrupts typing
      pending = true;
      toast.sticky("Hydra Notes updated. Reload to apply", {
        label: "Reload",
        onClick: applyUpdate,
      });
    },
    onOfflineReady() {
      toast.success("Ready to work offline");
    },
  });

  // ask the browser not to evict our IndexedDB under storage pressure
  if (navigator.storage?.persist) {
    void navigator.storage.persist();
  }

  // error tracking: lazy-loaded only when the server has a DSN configured
  void fetch("/api/config")
    .then((r) => (r.ok ? (r.json() as Promise<{ glitchtipDsn?: string }>) : null))
    .then(async (cfg) => {
      if (!cfg?.glitchtipDsn) return;
      const Sentry = await import("@sentry/react");
      Sentry.init({
        dsn: cfg.glitchtipDsn,
        release: `hydra-notes@${__APP_VERSION__}`,
        environment: import.meta.env.MODE,
      });
    })
    .catch(() => {});
}

/** Handle PWA shortcut + share-target launches. Called once from bootstrap. */
export function handleLaunchAction(): void {
  const params = new URLSearchParams(location.search);
  const action = params.get("action");
  const shareTitle = params.get("share_title");
  const shareText = params.get("share_text");
  const shareUrl = params.get("share_url");
  if (!action && !shareTitle && !shareText && !shareUrl) return;
  for (const k of ["action", "share_title", "share_text", "share_url"]) params.delete(k);
  const qs = params.toString();
  history.replaceState(null, "", location.pathname + (qs ? `?${qs}` : ""));

  setTimeout(() => {
    if (action === "new-note") createNote(null);
    if (action === "daily") void openDailyNote();
    if (shareTitle || shareText || shareUrl) {
      void (async () => {
        const { getData } = await import("@/data/store");
        const { openItem } = await import("@/commands/nav");
        const data = getData();
        await data.workspace.whenLoaded;
        const id = data.workspace.createItem("note", null, shareTitle || "Shared note");
        const body = [shareText, shareUrl].filter(Boolean).join("\n\n");
        if (body) {
          const handle = data.notes.open(id);
          await handle.whenLoaded;
          handle.doc.transact(() => {
            handle.text.insert(0, body + "\n");
          });
          handle.release();
        }
        openItem(id, { newTab: true });
      })();
    }
  }, 400);
}
